import React, { useState, useEffect, useRef } from 'react';
import {
  Truck,
  Server,
  User,
  Wifi,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  RefreshCw,
  Smartphone,
  Globe,
  Camera,
  X,
  QrCode,
  ShieldCheck,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { formatDriverLabel, sortDriversAlpha } from '../utils/dateUtils';

export default function DriverConnectPage({ onConnect, initialServerUrl = '', initialDriverId = '' }) {
  const defaultUrl = initialServerUrl || localStorage.getItem('loops_driver_server_url') || (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.hostname}:${window.location.port || '5000'}` : 'http://localhost:5000');

  const [serverUrl, setServerUrl] = useState(defaultUrl);
  const [selectedDriverId, setSelectedDriverId] = useState(initialDriverId || localStorage.getItem('loops_driver_id') || '');
  const [drivers, setDrivers] = useState([]);
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('idle'); // 'idle' | 'success' | 'error' | 'pairing'
  const [statusMessage, setStatusMessage] = useState('');
  const [serverDetails, setServerDetails] = useState(null);

  // QR Camera Scanner State
  const [isScanning, setIsScanning] = useState(false);
  const [scannerError, setScannerError] = useState('');
  const [showManualConfig, setShowManualConfig] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  // Clean server URL (remove trailing slash)
  const sanitizeUrl = (url) => {
    let clean = (url || '').trim();
    if (clean.endsWith('/')) clean = clean.slice(0, -1);
    if (!clean.startsWith('http://') && !clean.startsWith('https://')) {
      clean = 'http://' + clean;
    }
    return clean;
  };

  const getDeviceUuid = () => {
    let uuid = localStorage.getItem('loops_device_uuid');
    if (!uuid) {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        uuid = crypto.randomUUID();
      } else {
        uuid = 'dev_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
      }
      localStorage.setItem('loops_device_uuid', uuid);
    }
    return uuid;
  };

  const getDeviceName = () => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone Chauffeur';
    if (/iPad/.test(ua)) return 'iPad Chauffeur';
    if (/Android/.test(ua)) {
      const match = ua.match(/Android\s([0-9\.]*);\s([^;\)]*)/);
      return match && match[2] ? match[2].trim() : 'Android Smartphone';
    }
    return 'Web Mobile Client';
  };

  const getDevicePlatform = () => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
    if (/Android/.test(ua)) return 'android';
    return 'web';
  };

  // Perform Pairing Handshake with Backend
  const performPairing = async ({ pairingToken, tenantId = 1, driverId = null, server = null }) => {
    const targetServer = sanitizeUrl(server || serverUrl || window.location.origin);
    setConnectionStatus('pairing');
    setStatusMessage('Appairage du smartphone avec le serveur Loops...');

    try {
      const res = await fetch(`${targetServer}/api/driver/pair`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          pairing_token: pairingToken,
          tenant_id: parseInt(tenantId) || 1,
          driver_id: driverId ? parseInt(driverId) : null,
          device_uuid: getDeviceUuid(),
          device_name: getDeviceName(),
          device_platform: getDevicePlatform()
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Erreur serveur (${res.status})`);
      }

      const data = await res.json();
      setConnectionStatus('success');
      setStatusMessage(`Connecté avec succès en tant que ${data.driver.first_name} ${data.driver.last_name} (${data.tenant.name})`);

      // Save credentials in localStorage
      localStorage.setItem('loops_driver_server_url', targetServer);
      localStorage.setItem('loops_driver_id', String(data.driver.id));
      localStorage.setItem('loops_driver_auth_token', data.token);
      localStorage.setItem('loops_driver_tenant_id', String(data.tenant.id));
      localStorage.setItem('loops_driver_tenant_name', data.tenant.name);
      localStorage.setItem('loops_driver_profile', JSON.stringify(data.driver));

      // Launch app
      if (onConnect) {
        setTimeout(() => {
          onConnect(String(data.driver.id), targetServer, data.token, data.tenant, data.driver);
        }, 500);
      }
    } catch (err) {
      console.error('Erreur lors de l\'appairage:', err);
      setConnectionStatus('error');
      setStatusMessage(`Échec de l'appairage : ${err.message}`);
    }
  };

  // Check URL parameters for instant pairing on mount (e.g. from smartphone camera)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const tenantId = params.get('tenantId') || params.get('tenant');
    const driverId = params.get('driverId') || params.get('driver');
    const server = params.get('server');

    if (token) {
      performPairing({
        pairingToken: token,
        tenantId,
        driverId,
        server: server || window.location.origin
      });
    }
  }, []);

  // Camera QR Scanner logic
  const startCameraScanner = async () => {
    setIsScanning(true);
    setScannerError('');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.play();
      }

      // Check if native BarcodeDetector is available
      if (typeof window !== 'undefined' && 'BarcodeDetector' in window) {
        const barcodeDetector = new window.BarcodeDetector({ formats: ['qr_code'] });
        scanIntervalRef.current = setInterval(async () => {
          if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
            try {
              const barcodes = await barcodeDetector.detect(videoRef.current);
              if (barcodes && barcodes.length > 0) {
                const rawVal = barcodes[0].rawValue;
                handleQrDetected(rawVal);
              }
            } catch (e) {}
          }
        }, 300);
      } else {
        // Fallback info
        setScannerError('Votre navigateur ne supporte pas BarcodeDetector natif. Vous pouvez aussi scanner avec l\'appareil photo de votre smartphone pour ouvrir le lien directement.');
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setScannerError('Impossible d\'accéder à la caméra. Vérifiez les permissions dans votre navigateur.');
    }
  };

  const stopCameraScanner = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsScanning(false);
  };

  const handleQrDetected = (rawText) => {
    stopCameraScanner();
    try {
      // If it's a URL
      if (rawText.startsWith('http://') || rawText.startsWith('https://')) {
        const urlObj = new URL(rawText);
        const token = urlObj.searchParams.get('token');
        const tenantId = urlObj.searchParams.get('tenantId') || urlObj.searchParams.get('tenant') || '1';
        const driverId = urlObj.searchParams.get('driverId') || urlObj.searchParams.get('driver');
        const server = urlObj.origin;

        if (token) {
          performPairing({ pairingToken: token, tenantId, driverId, server });
          return;
        }
      }

      // If it's a JSON payload
      if (rawText.startsWith('{') && rawText.endsWith('}')) {
        const payload = JSON.parse(rawText);
        if (payload.token || payload.pairing_token) {
          performPairing({
            pairingToken: payload.token || payload.pairing_token,
            tenantId: payload.tenantId || payload.tenant_id || 1,
            driverId: payload.driverId || payload.driver_id,
            server: payload.server
          });
          return;
        }
      }

      // Plain token
      performPairing({ pairingToken: rawText.trim() });
    } catch (e) {
      setConnectionStatus('error');
      setStatusMessage('QR Code non reconnu ou format invalide.');
    }
  };

  // Clean up camera on unmount
  useEffect(() => {
    return () => {
      stopCameraScanner();
    };
  }, []);

  // Manual fallback fetching
  const testAndFetchDrivers = async (targetUrl = serverUrl) => {
    const cleanUrl = sanitizeUrl(targetUrl);
    setIsTesting(true);
    setConnectionStatus('testing');
    setStatusMessage('Connexion au serveur Loops en cours...');
    setDrivers([]);

    try {
      const response = await fetch(`${cleanUrl}/api/bootstrap?all=true`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Le serveur a répondu avec le code HTTP ${response.status}`);
      }

      const data = await response.json();
      const driverList = data.drivers || [];

      if (driverList.length === 0) {
        setConnectionStatus('error');
        setStatusMessage('Connecté au serveur, mais aucun chauffeur n\'est enregistré dans la base de données.');
        return;
      }

      setDrivers(driverList);
      setConnectionStatus('success');
      setStatusMessage(`Connecté avec succès ! (${driverList.length} chauffeur(s) disponible(s))`);
      setServerDetails({
        jobsCount: (data.jobs || []).length,
        trucksCount: (data.trucks || []).length
      });

      const savedId = localStorage.getItem('loops_driver_id');
      if (savedId && driverList.some(d => String(d.id) === String(savedId))) {
        setSelectedDriverId(savedId);
      } else if (driverList.length > 0) {
        setSelectedDriverId(String(driverList[0].id));
      }
    } catch (err) {
      console.error('Erreur de connexion au serveur Loops:', err);
      setConnectionStatus('error');
      setStatusMessage(`Impossible de joindre le serveur (${err.message}). Vérifiez l'adresse IP / URL.`);
    } finally {
      setIsTesting(false);
    }
  };

  const handleManualLaunch = () => {
    const cleanUrl = sanitizeUrl(serverUrl);
    if (!selectedDriverId) {
      alert('Veuillez sélectionner un chauffeur pour continuer.');
      return;
    }

    localStorage.setItem('loops_driver_server_url', cleanUrl);
    localStorage.setItem('loops_driver_id', String(selectedDriverId));

    if (onConnect) {
      onConnect(String(selectedDriverId), cleanUrl);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #090d16 0%, #0f172a 100%)',
      color: '#f8fafc',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '440px',
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '20px',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
        padding: '32px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '22px'
      }}>
        {/* App Logo & Header */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '68px',
            height: '68px',
            borderRadius: '18px',
            background: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 14px auto',
            boxShadow: '0 10px 25px rgba(56, 189, 248, 0.35)'
          }}>
            <Truck size={36} color="#0f172a" />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '800', margin: '0 0 6px 0', letterSpacing: '-0.5px' }}>
            Loops Chauffeur
          </h1>
          <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: 0, lineHeight: 1.4 }}>
            Appairage instantané sécurisé par QR Code
          </p>
        </div>

        {/* PRIMARY ACTION: QR SCANNER BUTTON */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            type="button"
            onClick={startCameraScanner}
            disabled={isScanning || connectionStatus === 'pairing'}
            style={{
              background: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)',
              color: '#0f172a',
              border: 'none',
              borderRadius: '14px',
              padding: '16px 20px',
              fontSize: '1rem',
              fontWeight: '800',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              boxShadow: '0 8px 24px rgba(56, 189, 248, 0.4)',
              transition: 'all 0.15s ease'
            }}
          >
            <Camera size={22} />
            <span>Scanner le QR Code Chauffeur</span>
          </button>

          <div style={{ fontSize: '0.74rem', color: '#94a3b8', textAlign: 'center', lineHeight: 1.4 }}>
            Demandez votre QR Code à votre dispatcheur dans <strong style={{ color: '#cbd5e1' }}>Base de données &gt; Livreurs &gt; Connexion QR</strong>
          </div>
        </div>

        {/* Status Banner */}
        {connectionStatus !== 'idle' && (
          <div style={{
            background: connectionStatus === 'success'
              ? 'rgba(34, 197, 94, 0.15)'
              : (connectionStatus === 'pairing' || connectionStatus === 'testing'
                ? 'rgba(56, 189, 248, 0.15)'
                : 'rgba(239, 68, 68, 0.15)'),
            border: `1px solid ${connectionStatus === 'success' ? '#22c55e' : (connectionStatus === 'pairing' || connectionStatus === 'testing' ? '#38bdf8' : '#ef4444')}`,
            borderRadius: '10px',
            padding: '10px 14px',
            fontSize: '0.78rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: connectionStatus === 'success' ? '#4ade80' : (connectionStatus === 'pairing' || connectionStatus === 'testing' ? '#38bdf8' : '#f87171')
          }}>
            {connectionStatus === 'success' ? (
              <CheckCircle2 size={18} style={{ flexShrink: 0 }} />
            ) : (connectionStatus === 'pairing' || connectionStatus === 'testing') ? (
              <RefreshCw size={18} style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }} />
            ) : (
              <AlertCircle size={18} style={{ flexShrink: 0 }} />
            )}
            <div style={{ lineHeight: '1.4', fontWeight: '600' }}>{statusMessage}</div>
          </div>
        )}

        {/* COLLAPSIBLE MANUAL CONFIGURATION */}
        <div style={{ borderTop: '1px solid #334155', paddingTop: '14px' }}>
          <button
            type="button"
            onClick={() => setShowManualConfig(!showManualConfig)}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              fontSize: '0.78rem',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '6px 0'
            }}
          >
            <span>Configuration manuelle (Développement)</span>
            {showManualConfig ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showManualConfig && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '12px' }}>
              {/* Server URL */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Server size={13} color="#38bdf8" />
                  <span>Adresse du Serveur Loops</span>
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="https://loops.thegreenfuture.ch"
                    value={serverUrl}
                    onChange={(e) => setServerUrl(e.target.value)}
                    style={{
                      flex: 1,
                      background: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      color: '#f8fafc',
                      fontSize: '0.82rem',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={() => testAndFetchDrivers(serverUrl)}
                    disabled={isTesting}
                    style={{
                      background: '#0284c7',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0 12px',
                      color: '#fff',
                      fontWeight: '700',
                      fontSize: '0.78rem',
                      cursor: isTesting ? 'wait' : 'pointer'
                    }}
                  >
                    {isTesting ? '...' : 'Tester'}
                  </button>
                </div>
              </div>

              {/* Driver Picker */}
              {drivers.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <User size={13} color="#38bdf8" />
                    <span>Sélectionner le chauffeur</span>
                  </label>
                  <select
                    value={selectedDriverId}
                    onChange={(e) => setSelectedDriverId(e.target.value)}
                    style={{
                      background: '#0f172a',
                      border: '1px solid #38bdf8',
                      borderRadius: '8px',
                      padding: '8px 10px',
                      color: '#f8fafc',
                      fontSize: '0.85rem',
                      fontWeight: '700'
                    }}
                  >
                    {sortDriversAlpha(drivers).map(d => (
                      <option key={d.id} value={d.id}>
                        👤 {formatDriverLabel(d)}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={handleManualLaunch}
                    disabled={!selectedDriverId}
                    style={{
                      marginTop: '6px',
                      background: '#22c55e',
                      color: '#0f172a',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px',
                      fontWeight: '800',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    Connexion manuelle directe →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer info */}
        <div style={{ textAlign: 'center', fontSize: '0.7rem', color: '#64748b' }}>
          <span>Loops Driver App Standalone · Multi-Tenant & Télématique</span>
        </div>
      </div>

      {/* FULLSCREEN CAMERA SCANNER OVERLAY */}
      {isScanning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: '#000000',
          zIndex: 999999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          {/* Top Bar with Close button */}
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            right: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 10
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#38bdf8', fontWeight: '800', fontSize: '0.95rem' }}>
              <QrCode size={20} />
              <span>Visez le QR Code</span>
            </div>
            <button
              onClick={stopCameraScanner}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              <X size={22} />
            </button>
          </div>

          {/* Video Container */}
          <div style={{
            position: 'relative',
            width: '100%',
            maxWidth: '360px',
            height: '360px',
            borderRadius: '24px',
            overflow: 'hidden',
            border: '2px solid #38bdf8',
            boxShadow: '0 0 30px rgba(56, 189, 248, 0.4)'
          }}>
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />

            {/* Viewfinder Target Guide */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '200px',
              height: '200px',
              border: '2px dashed rgba(255, 255, 255, 0.7)',
              borderRadius: '16px',
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)'
            }} />
          </div>

          {scannerError && (
            <div style={{
              marginTop: '20px',
              padding: '12px 18px',
              background: 'rgba(239, 68, 68, 0.2)',
              border: '1px solid #ef4444',
              borderRadius: '10px',
              color: '#f87171',
              fontSize: '0.8rem',
              maxWidth: '360px',
              textAlign: 'center'
            }}>
              {scannerError}
            </div>
          )}

          <div style={{
            marginTop: '24px',
            color: '#94a3b8',
            fontSize: '0.82rem',
            textAlign: 'center',
            maxWidth: '300px'
          }}>
            Placez le QR Code généré par votre dispatcheur au centre du viseur.
          </div>
        </div>
      )}
    </div>
  );
}
